import { useContext, useEffect, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { SearchContext } from "../contexts/SearchContext";
import { LoadingContext } from "../contexts/LoadingContext";
import { PaginationResponse } from "../objects/pagination";
import { Permission, Role } from "../models/system/role";
import { getPagination } from "../utils/helper";
import { errorToast } from "../utils/helper-ui";
import {
  createRole,
  getRoles,
  updateRole,
} from "../services/inventory/SystemApi";
import { Button, Checkbox, Dropdown, IconButton, Table, Tag } from "rsuite";
import PlusIcon from "@rsuite/icons/Plus";
import RoleForm from "../components/features/system/RoleForm";
import { BsCheck, BsCheckCircle } from "react-icons/bs";
const { Column, HeaderCell, Cell } = Table;

interface RolePageProps {}

const RolePage: FC<RolePageProps> = ({}) => {
  const { search, setSearch } = useContext(SearchContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(30);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role>();
  const [mode, setMode] = useState("admin");

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys.length === roles.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (checkedKeys.length > 0 && checkedKeys.length < roles.length) {
    indeterminate = true;
  }

  const handleCheckAll = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked ? roles.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked
      ? [...checkedKeys, value as string]
      : checkedKeys.filter((item) => item !== (value as string));
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, search, page, size, mode]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getRoles({ page, size, search }, mode);
      const resJson = await res.json();
      //   console.log(data);
      setRoles((prev) => [...prev, ...resJson.data.items]);
      setPagination(getPagination(resJson.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Role</h1>
      {/* button */}
      <div className=" flex flex-row gap-2">
        <Dropdown
          appearance="primary"
          size="md"
          title={mode === "admin" ? "Admin" : "Merchant"}
        >
          <Dropdown.Item
            onClick={() => {
              setRoles([]);
              setMode("admin");
            }}
          >
            Admin
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              setRoles([]);
              setMode("merchant");
            }}
          >
            Merchant
          </Dropdown.Item>
        </Dropdown>
        <IconButton
          size="sm"
          appearance="primary"
          color="green"
          icon={<PlusIcon />}
          onClick={() => setModalFormOpen(true)}
        >
          Create Role
        </IconButton>
      </div>
    </div>
  );
  const saveRole = async (val: Role) => {
    try {
      setIsLoading(true);
      if (val.id == "") {
        await createRole(val);
      } else {
        await updateRole(val.id!, val);
      }
      fetchData();
      setModalFormOpen(false);
      setRoles([]);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <MainLayout header={renderHeader()}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th
                className="px-4 py-2 text-left border bg-gray-50"
                style={{ width: 100 }}
              >
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                />
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Name</th>
              <th className="px-4 py-2 text-left border bg-gray-50">
                Super Admin
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Owner</th>
              {/* <th className="px-4 py-2 text-left border bg-gray-50">
                Permission
              </th> */}
            </tr>
          </thead>
          <tbody>
            {roles.map((rowData, i) => (
              <tr
                key={rowData.id}
                className="hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  setSelectedRole(rowData);
                  setModalFormOpen(true);
                }}
              >
                <td className="px-4 py-2 border">
                  <Checkbox
                    value={rowData.id}
                    inline
                    onChange={handleCheck}
                    checked={checkedKeys.some((item) => item === rowData.id)}
                  />
                  {i + 1}
                </td>
            
                <td className="px-4 py-2 border">{rowData.name}</td>
                <td className="px-4 py-2 border">
                  {rowData.is_super_admin ? (
                    <BsCheckCircle className="text-green-500" size={16} />
                  ) : (
                    ""
                  )}
                </td>
                <td className="px-4 py-2 border">
                  {rowData.is_owner ? (
                    <BsCheckCircle className="text-green-500" size={16} />
                  ) : (
                    ""
                  )}
                </td>
                {/* <td className="px-4 py-2 border">
                  {(rowData.permissions ?? []).map((item) => (
                    <span key={item.id} className="mr-2">
                      {item.name}
                    </span>
                  ))}
                </td> */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!pagination?.last && (
        <div className="flex justify-center my-2">
          <Button
            onClick={() => {
              setPage((pagination?.page ?? 0) + 1);
            }}
          >
            Load more
          </Button>
        </div>
      )}
      {/* <Table minHeight={700} data={roles} onRowClick={(data, i) => {}}>
        <Column width={50} align="center" fixed>
          <HeaderCell style={{ padding: 0 }}>
            <div style={{ lineHeight: "40px" }}>
              <Checkbox
                inline
                checked={checked}
                indeterminate={indeterminate}
                onChange={handleCheckAll}
              />
            </div>
          </HeaderCell>
          <Cell style={{ padding: 0 }}>
            {(rowData) => (
              <div style={{ lineHeight: "46px" }}>
                <Checkbox
                  value={rowData.id}
                  inline
                  onChange={handleCheck}
                  checked={checkedKeys.some((item) => item === rowData.id)}
                />
              </div>
            )}
          </Cell>
        </Column>
        <Column width={70} align="center" fixed>
          <HeaderCell>No</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>
                {(page - 1) * size + roles.indexOf(rowData as Role) + 1}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={200} resizable fixed>
          <HeaderCell>Name</HeaderCell>
          <Cell>
            {(rowData) => (
              <div
                className="hover:underline cursor-pointer font-semibold "
                onClick={() => {
                  setSelectedRole(rowData as Role);
                  setModalFormOpen(true);
                }}
              >
                {rowData.name}
              </div>
            )}
          </Cell>
        </Column>
        <Column width={100} resizable align="center">
          <HeaderCell>Super Admin</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>
                {rowData.is_super_admin ? (
                  <BsCheckCircle className="text-green-500" size={16} />
                ) : (
                  ""
                )}
              </div>
            )}
          </Cell>
        </Column>
        <Column width={100} resizable align="center">
          <HeaderCell>Owner</HeaderCell>
          <Cell>
            {(rowData) => (
              <div>
                {rowData.is_owner ? (
                  <BsCheckCircle className="text-green-500" size={16} />
                ) : (
                  ""
                )}
              </div>
            )}
          </Cell>
        </Column>
        <Column width={600} resizable>
          <HeaderCell>Permission</HeaderCell>
          <Cell>
            {(rowData) =>
              rowData.permissions.map((item: Permission) => (
                <Tag size="sm" key={item.id}>
                  {item.name}
                </Tag>
              ))
            }
          </Cell>
        </Column>
      </Table> */}
      <RoleForm
        open={modalFormOpen}
        role={selectedRole}
        onClose={() => setModalFormOpen(false)}
        onSubmit={(val) => {
          saveRole(val);
          setModalFormOpen(false);
        }}
      />
    </MainLayout>
  );
};
export default RolePage;
